import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Icon } from "./icons";
import { type PaletteItem, paletteItems, type TargetCard } from "./model";

/**
 * Quick search (Ctrl/⌘ K, or "/"): type part of a site's name, address or client, or a view,
 * and Enter goes there. A combobox over a listbox: arrows move, Escape closes.
 */
export function Palette({
  cards,
  views,
  onClose,
}: {
  cards: readonly TargetCard[];
  views: readonly { id: string; label: string; href: string }[];
  onClose(): void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const items = paletteItems(cards, views, query);

  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
  }, []);

  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    window.location.hash = item.href.slice(1);
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(items[active]);
    }
  };

  return (
    <dialog
      ref={dialog}
      className="palette"
      aria-label="Quick search"
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="palette-box">
        <div className="palette-input">
          <Icon name="search" />
          <input
            // biome-ignore lint/a11y/noAutofocus: the palette exists to be typed into
            autoFocus
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={items[active] ? `palette-${items[active].key}` : undefined}
            aria-autocomplete="list"
            placeholder="Site, address, client or view…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
          />
          <kbd>Esc</kbd>
        </div>
        <div id="palette-list" role="listbox" className="palette-list" aria-label="Results">
          {items.length === 0 && <p className="muted palette-empty">Nothing matches.</p>}
          {items.map((item, i) => (
            <div
              key={item.key}
              id={`palette-${item.key}`}
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              className="palette-item"
              data-status={item.status}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                go(item);
              }}
            >
              {item.status ? <Icon name={item.status} /> : <Icon name="arrow" />}
              <span className="palette-label">{item.label}</span>
              <span className="palette-hint muted ellipsis">{item.hint}</span>
            </div>
          ))}
        </div>
      </div>
    </dialog>
  );
}
